/* eslint-disable max-lines */
import React from 'react';
import { Prompt } from 'react-router';
import _ from 'lodash';
import { withTheme } from 'styled-components';
import type { Moment } from 'moment';
import moment from 'moment';
import { styled } from '@this/constants/themes';
import { ButtonBase } from '@this/components/shared/atoms/button';
import Text from '@this/components/shared/text/text';

import ContentHeader from '@this/components/shared/content_header/content_header';
import SimpleLoading from '@this/components/shared/simple_loading/simple_loading';

import User from '@this/domain/user/user';
import NonOrderItem from '@this/domain/non_order_item';
import TransportElement from '@this/domain/transport_element';
import SelectRepository from '@this/domain/select_repository';
import SelectStore from '@this/domain/select_store';
import SearchQuery from '@this/domain/search_query';
import type { AvailableRepository } from '@this/domain/available_repository';
import type { RouteComponentProps } from 'react-router-dom';
import Trip from '@this/src/domain/trip/trip';
import TravelerList from '@this/src/domain/traveler/traveler_list';
import Traveler from '@this/src/domain/traveler/traveler';
import { Fetcher } from '@this/src/util';
import type OrderItem from '@this/domain/order_item';
import { reportError } from '@this/lib/bugsnag';
import { Wrap, BodyWrap, Body, LoadingWrap } from './trips';
import TripsFormBasicinfoTemplate from './trips_form_block/trips_form_basicinfo.template';
import type { AirlineName } from './trips_form_block/trips_form_itemform.template';
import TripsFormItemformTemplate from './trips_form_block/trips_form_itemform.template';

interface Props extends RouteComponentProps<Record<string, string | undefined>, Record<string, any>, any> {
  serviceId: number;
  availableRepos: AvailableRepository[];
  theme: { themeClass: string };
}

interface TripsDetailState {
  serviceId: number | undefined;
  trip: any;
  user: any;
  members: any;
  travelers: any;
  title: string;
  loading: boolean;
  shouldBlockNavigation: boolean;
  validationErrors: any;
  outdate: Moment;
  homedate: Moment;
  query: SearchQuery | undefined;
  store: SelectStore | undefined;
  repository: SelectRepository | undefined;
  domesticAirlines: AirlineName[];
  internationalAirlines: AirlineName[];
  showItemForm: boolean;
  selectedItemType: string | null;
  creatingNonOrderItem: NonOrderItem | null;
  editingNonOrderItem: NonOrderItem | null;
  error: string | null;
  showSearchBox: boolean;
  tripItemFormTemplateErrMsg: string;
  shortdistance: boolean;
  preventZeroAmountApplication: boolean;
}

interface TripsDetailResponse {
  trip: any;
  user: any;
  members: any[];
  query: any;
  shortdistance: boolean;
  prevent_zero_amount_application: boolean;
}

interface TripFormErrors {
  hotelDeadline: string | null;
  zeroAmount: string | null;
  errors: string[];
}

class TripsDetailForm extends React.Component<Props, TripsDetailState> {
  constructor(props: Props) {
    super(props);
    const lState = props.location.state;

    this.state = {
      serviceId: props.serviceId,
      trip: null,
      user: null,
      members: [],
      travelers: [],
      title: '',
      loading: true,
      shouldBlockNavigation: true,
      validationErrors: {},
      outdate: lState && lState.outdate ? moment(lState.outdate) : moment().add(1, 'days'),
      homedate: lState && lState.homedate ? moment(lState.homedate) : moment().add(2, 'days'),
      query: undefined,
      store: undefined,
      repository: undefined,
      domesticAirlines: [],
      internationalAirlines: [],
      showItemForm: true,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null,
      error: null,
      showSearchBox: false,
      tripItemFormTemplateErrMsg: '',
      shortdistance: false,
      preventZeroAmountApplication: false
    };
  }

  componentDidMount() {
    const lState = this.props.location.state;
    Fetcher.get<TripsDetailResponse>(`/trips/${this.props.match.params.id}/detail.json`, lState || {}).then(
      result => {
        const trip = new Trip(result.trip);
        const user = new User(result.user);
        const members = _.map(result.members, m => new User(m));
        let travelers: any;
        if (trip) {
          travelers = TravelerList.fromTravelerInformations({
            informations: trip.travelerInformations,
            user,
            members,
            peoplenum: trip.travelerInformations.list.length
          });
        } else {
          travelers = TravelerList.fromCount(1);
          travelers.setTravelerAtIndex(0, new Traveler(result.user));
        }
        const query = result.query
          ? new SearchQuery(
              _.merge(result.query, {
                cabin: user.defaultSeatClasses.join(','),
                travelers,
                time: {
                  startTime: result.query.departure_time || trip.startTime,
                  endTime: result.query.return_time || trip.endTime
                },
                availableRepos: this.props.availableRepos
              })
            )
          : new SearchQuery({
              cabin: user.defaultSeatClasses.join(','),
              peoplenum: travelers.list.length,
              travelers,
              time: {
                startTime: trip.startTime,
                endTime: trip.endTime
              },
              availableRepos: this.props.availableRepos
            });
        let outdate;
        if (trip.cacheStartTime) {
          outdate = moment(trip.cacheStartTime);
        } else if (trip.startTime) {
          outdate = moment(trip.startTime);
        } else {
          outdate = moment(trip.currentOrder.startDate());
        }

        let homedate;
        if (trip.cacheEndTime) {
          homedate = moment(trip.cacheEndTime);
        } else if (trip.endTime) {
          homedate = moment(trip.endTime);
        } else {
          homedate = moment(trip.currentOrder.endDate());
        }
        this.fetchAirlines();

        this.setState(
          {
            trip,
            user,
            members,
            travelers,
            title: trip.title,
            outdate,
            homedate,
            query,
            loading: false,
            shortdistance: result.shortdistance,
            preventZeroAmountApplication: result.prevent_zero_amount_application
          },
          () => {
            if (trip.draft && result.query && query) {
              this.handleSmartSearch(query);
            }
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  componentDidUpdate() {
    // 未保存離脱の警告をPromptで実装しているが、再読み込みとタブの終了に対応していないため
    if (this.state.shouldBlockNavigation) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  async fetchAirlines() {
    const domestic = await Fetcher.get<{ airlines: AirlineName[] }>('/domestic_airlines.json');
    const international = await Fetcher.get<{ airlines: AirlineName[] }>('/international_airlines.json');
    this.setState({
      domesticAirlines: domestic.airlines,
      internationalAirlines: international.airlines
    });
  }

  addedItems = () => {
    let items = utils.dig(this.state.trip, 'nonOrderItems') || [];
    if (utils.dig(this.state.trip, 'draft')) {
      items = items.concat(utils.dig(this.state.store, 'result', 'items') || []);
    } else {
      items = items.concat(utils.dig(this.state.trip, 'order', 'orderItems') || []);
    }

    return _.sortBy(items, item => item.startDateTime());
  };

  handleShowItemForm = () => {
    this.setState({
      showItemForm: true,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  handleSmartSearch = (query: SearchQuery) => {
    Fetcher.post('/search_histories', query.searchHistoryParams());
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.query) {
          this.props.history.push(`${location.pathname}${query.getQueryString()}`);

          const store = new SelectStore({ query: this.state.query }, this.state.user, this.props.availableRepos);

          this.setState(
            {
              store,
              repository: new SelectRepository({ store })
            },
            () => {
              if (this.state.repository) {
                this.state.repository.startSearch(this.props.theme.themeClass);
              }
            }
          );
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  };

  selectItemType = (elementType: string) => {
    if (this.state.trip) {
      const item = new NonOrderItem({ elementType, peopleNum: this.state.travelers.list.length, supplier: '' });
      const startTime = this.state.outdate.format('YYYY-MM-DD HH:mm');
      const endTime = this.state.homedate.format('YYYY-MM-DD HH:mm');
      item.handleAddElementByType(elementType, startTime, endTime);
      if (elementType !== 'hotel') {
        item.transports[0].handleTransportTypeChange(elementType);
      }
      this.setState({
        selectedItemType: elementType,
        creatingNonOrderItem: item
      });
    }
  };

  isSelectItemTypeClass = (elementType: string) => {
    if (this.state.selectedItemType === elementType) {
      return 'trip_form_item_selected';
    }
    return '';
  };

  deselectItemType = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.setState({
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  addNonOrderItemToTrip = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!this.state.creatingNonOrderItem) {
      return;
    }
    const trip = this.state.trip;

    const totalPrice =
      this.state.creatingNonOrderItem.price.totalPrice() * this.state.creatingNonOrderItem.peopleNum;
    if (isNaN(totalPrice)) {
      this.setState({ tripItemFormTemplateErrMsg: '金額を入力してください' });
      return;
    }
    if (totalPrice === 0 && this.state.preventZeroAmountApplication) {
      this.setState({ tripItemFormTemplateErrMsg: '金額は1円以上で入力してください' });
      return;
    }

    if (this.state.editingNonOrderItem) {
      trip.nonOrderItems = _.filter(trip.nonOrderItems, item => item !== this.state.editingNonOrderItem);
    }
    trip.nonOrderItems.push(this.state.creatingNonOrderItem);
    this.setState({
      trip,
      showItemForm: false,
      selectedItemType: null,
      creatingNonOrderItem: null,
      tripItemFormTemplateErrMsg: ''
    });
  };

  static handleTimeChange(item: NonOrderItem, type: 'from' | 'to', time: Moment) {
    item.transports[0][type].handleTimeChange(time);
    if (item.transports[0].from.time > item.transports[0].to.time) {
      item.transports[0][type === 'from' ? 'to' : 'from'].handleTimeChange(item.transports[0][type].time);
    }
  }

  startEditingNonOrderItem = (item: NonOrderItem) => {
    const element = item.elements[0];
    this.setState({
      showItemForm: true,
      selectedItemType: element instanceof TransportElement ? element.transportType : element.type,
      creatingNonOrderItem: _.cloneDeep(item),
      editingNonOrderItem: item
    });
  };

  toggleNonOrderItemNeed = (item: NonOrderItem) => {
    item.toggleNeed();
    if (this.state.editingNonOrderItem === item) {
      this.setState({
        selectedItemType: null,
        creatingNonOrderItem: null,
        editingNonOrderItem: null
      });
    }
  };

  handleSelectItem = () => {
    this.updateUrl();
  };

  private updateUrl() {
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.store && this.state.query) {
          let href = location.pathname;
          href += this.state.query.getQueryString();
          if (this.state.store.result) {
            href += this.state.store.result.getQueryString();
          }
          this.props.history.push(href);
          const params = utils.getParams();
          _.map(params.items, (item, i) => {
            if (item.item_type === 'hotel') {
              _.merge(item, {
                prefecture: this.state.repository ? this.state.repository.getPrefecture(Number(i)) : ''
              });
            }
            return item;
          });
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  }

  handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
    const validationErrors = this.validationErrors();
    if (_.isEmpty(validationErrors)) {
      this.submit();
    } else {
      e.preventDefault();
      this.setState({ validationErrors });
    }
  };

  submit() {
    Fetcher.put(`/trips/${this.state.trip.id}/detail`, this.submitParams()).then(
      () => {
        this.setState(
          {
            shouldBlockNavigation: false
          },
          () => {
            this.props.history.push('/trips');
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  submitParams() {
    return {
      params: {
        title: this.state.title,
        travelers: this.state.travelers.getTravelersParam(),
        departure_time: this.state.outdate.format('YYYY-MM-DD HH:mm ZZ'),
        return_time: this.state.homedate.format('YYYY-MM-DD HH:mm ZZ'),
        non_order_items: _.compact(_.map(this.state.trip.nonOrderItems, item => item.updateParams())),
        search_result: this.state.store ? this.state.store.result.confirmParams() : {}
      },
      query: this.state.store && this.state.query ? this.state.query.getSubmitParams() : {}
    };
  }

  validZeroAmount = () => {
    if (!this.state.preventZeroAmountApplication) {
      return true;
    }
    const nonOrderItems: NonOrderItem[] = utils.dig(this.state.trip, 'nonOrderItems') || [];
    const neededNonOrderItems = nonOrderItems.filter(item => item.isNeed);

    const orderItems: OrderItem[] = utils.dig(this.state.trip, 'order', 'orderItems') || [];
    return neededNonOrderItems.length > 0 || orderItems.length > 0;
  };

  validationErrors() {
    const errors: TripFormErrors = {
      hotelDeadline: null,
      zeroAmount: null,
      errors: []
    };
    errors.zeroAmount = this.validZeroAmount()
      ? null
      : '0円での申請は設定で禁止されています。旅程内容と金額を1つ以上入力してください。';
    return utils.compactObject(errors);
  }

  toggleShowSearchBox = () => {
    this.setState({ showSearchBox: !this.state.showSearchBox });
  };

  handleOpenShowSearchBox = () => {
    this.setState({ showSearchBox: true });
  };

  changeSmartSearch = () => {};

  handleTravelerSelect = (i: number, traveler: any) => {};

  static requiredError(name: string, value: string, message = 'を入力してください') {
    if (!value || value.length === 0) {
      return name + message;
    }
    return null;
  }

  static katakanaError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください`;
    }
    return null;
  }

  static alphabetError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return null;
  }

  static dateError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!moment(value).isValid()) {
      return `${name}を正しく入力してください（例：1986-01-01）`;
    }
    return null;
  }

  render() {
    const {
      serviceId,
      trip,
      title,
      user,
      members,
      travelers,
      loading,
      shouldBlockNavigation,
      validationErrors,
      outdate,
      homedate,
      query,
      store,
      repository,
      domesticAirlines,
      internationalAirlines,
      showItemForm,
      selectedItemType,
      creatingNonOrderItem,
      editingNonOrderItem,
      error,
      showSearchBox,
      tripItemFormTemplateErrMsg,
      shortdistance
    } = this.state;
    try {
      return (
        <Wrap>
          <Prompt
            when={shouldBlockNavigation}
            message="保存されていないデータが失われます。本当によろしいですか？"
          />
          <FormBodyWrap>
            <div>
              <FormBody>
                <ContentHeader title="旅程情報詳細" backButton backLink="/trips" />
                <TripForm>
                  <TripFormLeft>
                    {loading ? (
                      <LoadingWrap>
                        <SimpleLoading />
                      </LoadingWrap>
                    ) : error ? (
                      <LoadingWrap>
                        <Error>{error}</Error>
                      </LoadingWrap>
                    ) : (
                      <TripsFormBasicinfoTemplate
                        editable={false}
                        travelers={travelers}
                        user={user}
                        title={title}
                        members={members}
                        outdate={outdate}
                        homedate={homedate}
                        handleTravelerSelect={this.handleTravelerSelect}
                        validationErrors={validationErrors}
                      />
                    )}
                  </TripFormLeft>
                  <TripFormRight>
                    <Section>
                      <SectionIn>
                        {(trip === null || trip.draft || trip.tripDetailEditable()) && (
                          <SubmitButton onClick={e => this.handleSubmit(e)}>保存</SubmitButton>
                        )}
                        {!_.isEmpty(validationErrors.hotelDeadline) && (
                          <Error>
                            <Text text={validationErrors.hotelDeadline} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.zeroAmount) && (
                          <Error>
                            <Text text={validationErrors.zeroAmount} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.errors) && (
                          <Error>
                            <Text text={validationErrors.errors.join('\n')} />
                          </Error>
                        )}
                      </SectionIn>
                    </Section>
                  </TripFormRight>
                </TripForm>
                <TripsFormItemformTemplate
                  serviceId={serviceId}
                  user={user}
                  trip={trip}
                  query={query}
                  store={store}
                  repository={repository}
                  domesticAirlines={domesticAirlines}
                  internationalAirlines={internationalAirlines}
                  addedItems={this.addedItems()}
                  editingNonOrderItem={editingNonOrderItem}
                  creatingNonOrderItem={creatingNonOrderItem}
                  selectedItemType={selectedItemType}
                  selectItemType={this.selectItemType}
                  isSelectItemTypeClass={this.isSelectItemTypeClass}
                  deselectItemType={this.deselectItemType}
                  startEditingNonOrderItem={this.startEditingNonOrderItem}
                  toggleNonOrderItemNeed={this.toggleNonOrderItemNeed}
                  handleSelectItem={this.handleSelectItem}
                  handleOpenShowSearchBox={this.handleOpenShowSearchBox}
                  handleShowItemForm={this.handleShowItemForm}
                  toggleShowSearchBox={this.toggleShowSearchBox}
                  changeSmartSearch={this.changeSmartSearch}
                  handleSmartSearch={this.handleSmartSearch}
                  addNonOrderItemToTrip={this.addNonOrderItemToTrip}
                  handleTimeChange={TripsDetailForm.handleTimeChange}
                  tripItemFormTemplateErrMsg={tripItemFormTemplateErrMsg}
                  isSmartSearching={false}
                  showItemForm={showItemForm}
                  showSearchBox={showSearchBox}
                  shortdistance={shortdistance}
                  useSmartSearch={false}
                />
              </FormBody>
            </div>
            <FooterActionGroup>
              <FooterButtonArea>
                {(trip === null || trip.draft || trip.tripDetailEditable()) && (
                  <SubmitButton onClick={e => this.handleSubmit(e)}>
                    {trip === null || trip.draft ? '次へ' : '保存'}
                  </SubmitButton>
                )}
              </FooterButtonArea>
            </FooterActionGroup>
          </FormBodyWrap>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const FormBodyWrap = styled(BodyWrap)`
  padding: 20px 0;
`;

const FormBody = styled(Body)`
  padding: 0;
`;

const TripForm = styled.div`
  display: flex;
  padding: 20px;
`;

const TripFormLeft = styled.div`
  width: 80%;
`;

const TripFormRight = styled.div`
  margin: 0 0 0 auto;
`;

const Section = styled.div`
  padding-bottom: 20px;
`;

const SectionIn = styled.div`
  margin-left: 10px;
  font-size: 13px;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const SubmitButton = styled.button`
  ${ButtonBase};
  font-size: 13px;
  width: 200px;
`;

const FooterActionGroup = styled.div`
  background: #fff;
  position: sticky;
  bottom: 0;
  box-shadow: 0 -3px 3px 3px rgba(0, 0, 0, 0.1);
`;

const FooterButtonArea = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
`;

export default withTheme(TripsDetailForm);
